import React, { useEffect, useMemo, useState } from "react";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PrintIcon from "@mui/icons-material/Print";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import dayjs from "dayjs";

import PageHeader from "../../app/layout/PageHeader";
import { api } from "../../api/apiClient";
import AiTextImprovement from "../../components/ai/AiTextImprovement";
import { useAuth } from "../../context/AuthContext";
import AppDatePicker from "../components-ui/AppDatePicker";
import { notify } from "../components-ui/toast";
import "../../css/buttons.css";
import "../../css/cards.css";
import "../../css/forms.css";
import "./SessionSummaryReportPage.css";

interface Service {
  serviceId: number;
  name: string;
}

interface ServiceInstance {
  serviceInstanceId: number;
  startDateTime: string;
  endDateTime: string;
  status: string;
}

interface Retrospective {
  serviceInstanceId: number;
  outcomes?: string | null;
  whatWorkedWell?: string | null;
  challenges?: string | null;
  lessonsLearned?: string | null;
  followUpActions?: string | null;
  lastUpdatedDate?: string | null;
}

type SummaryFields = Omit<
  Retrospective,
  "serviceInstanceId" | "lastUpdatedDate"
>;

interface SavedSessionSummary extends SummaryFields {
  serviceSessionSummaryId: number;
  serviceId: number;
  serviceInstanceIds: number[];
  periodFrom: string;
  periodTo: string;
  sessionCount: number;
  createdDate: string;
  lastUpdatedDate?: string | null;
}

const emptySummary: SummaryFields = {
  outcomes: "",
  whatWorkedWell: "",
  challenges: "",
  lessonsLearned: "",
  followUpActions: "",
};

const fields: Array<{ key: keyof SummaryFields; label: string }> = [
  { key: "outcomes", label: "Outcomes" },
  { key: "whatWorkedWell", label: "What worked well" },
  { key: "challenges", label: "Challenges" },
  { key: "lessonsLearned", label: "Lessons learned" },
  { key: "followUpActions", label: "Follow-up actions" },
];

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  day: "2-digit",
  month: "short",
  year: "2-digit",
});

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});

export default function SessionSummaryReportPage() {
  const { selectedTenant } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [serviceId, setServiceId] = useState<number | null>(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [instances, setInstances] = useState<ServiceInstance[]>([]);
  const [retrospectives, setRetrospectives] = useState<
    Record<number, Retrospective>
  >({});
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [summary, setSummary] = useState<SummaryFields>(emptySummary);
  const [summaryId, setSummaryId] = useState<number | null>(null);
  const [savedSummaries, setSavedSummaries] = useState<SavedSessionSummary[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedTenant?.tenantId) return;
    api<Service[]>(`/api/services?tenantId=${selectedTenant.tenantId}`)
      .then(setServices)
      .catch(() => setError("Failed to load services."));
  }, [selectedTenant?.tenantId]);

  useEffect(() => {
    setInstances([]);
    setRetrospectives({});
    setSelectedIds([]);
    setSummary(emptySummary);
    setSummaryId(null);
    setError(null);
    if (!serviceId) return;

    setLoading(true);
    api<ServiceInstance[]>(`/api/services/${serviceId}/instances`)
      .then(async (items) => {
        const ordered = [...items].sort(
          (a, b) =>
            new Date(b.startDateTime).getTime() -
            new Date(a.startDateTime).getTime(),
        );
        const results = await Promise.all(
          ordered.map((item) =>
            api<Retrospective>(
              `/api/serviceinstances/${item.serviceInstanceId}/retrospective`,
            ),
          ),
        );
        setInstances(ordered);
        setRetrospectives(
          Object.fromEntries(
            results.map((item) => [item.serviceInstanceId, item]),
          ),
        );
      })
      .catch(() => setError("Failed to load session retrospectives."))
      .finally(() => setLoading(false));
  }, [serviceId]);

  useEffect(() => {
    setSavedSummaries([]);
    if (!serviceId) return;
    api<SavedSessionSummary[]>(`/api/services/${serviceId}/session-summaries`)
      .then(setSavedSummaries)
      .catch(() => setError("Failed to load saved session summaries."));
  }, [serviceId]);

  const availableInstances = useMemo(
    () =>
      instances.filter((item) => {
        const sessionDate = new Date(item.startDateTime);
        const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
        const to = toDate ? new Date(`${toDate}T23:59:59.999`) : null;
        const retrospective = retrospectives[item.serviceInstanceId];
        return (
          (!from || sessionDate >= from) &&
          (!to || sessionDate <= to) &&
          retrospective &&
          fields.some(({ key }) => Boolean(retrospective[key]?.trim()))
        );
      }),
    [fromDate, instances, retrospectives, toDate],
  );
  const selectedService = useMemo(
    () => services.find((service) => service.serviceId === serviceId),
    [serviceId, services],
  );
  const summaryCreated = fields.some(({ key }) => Boolean(summary[key]));
  const aiSourceFields = useMemo(
    () => fields.map(({ key, label }) => ({
      name: key,
      label,
      value: instances
        .filter((item) => selectedIds.includes(item.serviceInstanceId))
        .map((item) => {
          const value = retrospectives[item.serviceInstanceId]?.[key]?.trim();
          return value ? `${formatDate(item.startDateTime)}\n${value}` : "";
        })
        .filter(Boolean)
        .join("\n\n"),
    })),
    [instances, retrospectives, selectedIds],
  );

  useEffect(() => {
    const availableIds = new Set(
      availableInstances.map((item) => item.serviceInstanceId),
    );
    setSelectedIds((current) => {
      const filtered = current.filter((id) => availableIds.has(id));
      return filtered.length === current.length ? current : filtered;
    });
  }, [availableInstances]);

  const toggle = (id: number) =>
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );

  const generateSummary = () => {
    const selected = instances.filter((item) =>
      selectedIds.includes(item.serviceInstanceId),
    );
    const generated = fields.reduce<SummaryFields>(
      (result, { key }) => {
        result[key] = selected
          .map((item) => {
            const value = retrospectives[item.serviceInstanceId]?.[key]?.trim();
            return value ? `${formatDate(item.startDateTime)}\n${value}` : "";
          })
          .filter(Boolean)
          .join("\n\n");
        return result;
      },
      { ...emptySummary },
    );
    setSummary(generated);
    setSummaryId(null);
  };

  const changeSessions = () => {
    setSummary(emptySummary);
    setSummaryId(null);
    setSelectedIds([]);
  };

  const openSavedSummary = (saved: SavedSessionSummary) => {
    setSelectedIds(saved.serviceInstanceIds);
    setSummaryId(saved.serviceSessionSummaryId);
    setSummary({
      outcomes: saved.outcomes ?? "",
      whatWorkedWell: saved.whatWorkedWell ?? "",
      challenges: saved.challenges ?? "",
      lessonsLearned: saved.lessonsLearned ?? "",
      followUpActions: saved.followUpActions ?? "",
    });
  };

  const saveSummary = async () => {
    if (!serviceId || selectedIds.length === 0 || saving) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await api<SavedSessionSummary>(
        summaryId
          ? `/api/services/${serviceId}/session-summaries/${summaryId}`
          : `/api/services/${serviceId}/session-summaries`,
        {
          method: summaryId ? "PUT" : "POST",
          body: { serviceInstanceIds: selectedIds, ...summary },
        },
      );
      setSummaryId(saved.serviceSessionSummaryId);
      setSavedSummaries((current) => [
        saved,
        ...current.filter((item) => item.serviceSessionSummaryId !== saved.serviceSessionSummaryId),
      ]);
      notify.success(summaryId ? "Session summary updated." : "Session summary saved.");
    } catch {
      setError("The session summary could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container session-summary-page">
      <PageHeader
        title={selectedService?.name ?? "Session Retrospective Summary"}
        subtitle={selectedService ? "Session Retrospective Summary" : "Combine retrospectives into one editable report."}
      />

      {error && <div className="notification notification--error">{error}</div>}

      <section className="session-summary-workspace">
        {!summaryCreated ? (
          <>
            <div className="session-summary-workspace-heading">
              <div>
                <h2>Retrospective overview</h2>
                <p>Choose a service and the completed sessions to include.</p>
              </div>
            </div>

            <div className="session-summary-service-row">
              <div className="form-group session-summary-service-filter">
                <label className="form-label" htmlFor="summary-service">Service</label>
                <select
                  id="summary-service"
                  className="form-input"
                  value={serviceId ?? ""}
                  onChange={(event) => setServiceId(event.target.value ? Number(event.target.value) : null)}
                >
                  <option value="">Select a service</option>
                  {services.map((service) => <option key={service.serviceId} value={service.serviceId}>{service.name}</option>)}
                </select>
              </div>
            </div>

            <div className="session-summary-date-range">
              <div className="form-group">
                <label className="form-label">From</label>
                <AppDatePicker value={fromDate} maxDate={toDate ? dayjs(toDate) : undefined} onChange={(value) => setFromDate(value ?? "")} />
              </div>
              <div className="form-group">
                <label className="form-label">To</label>
                <AppDatePicker value={toDate} minDate={fromDate ? dayjs(fromDate) : undefined} onChange={(value) => setToDate(value ?? "")} />
              </div>
            </div>

            {savedSummaries.length > 0 && (
              <div className="session-summary-saved">
                <div className="session-summary-saved-label">Saved summaries</div>
                <div className="session-summary-saved-list">
                  {savedSummaries.slice(0, 5).map((saved) => (
                    <button
                      type="button"
                      className="session-summary-saved-item"
                      key={saved.serviceSessionSummaryId}
                      onClick={() => openSavedSummary(saved)}
                    >
                      <span>{formatDate(saved.periodFrom)} – {formatDate(saved.periodTo)}</span>
                      <small>{saved.sessionCount} session{saved.sessionCount === 1 ? "" : "s"}</small>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loading && <div className="field-hint">Loading retrospectives…</div>}
            {!loading && serviceId && availableInstances.length === 0 && <div className="field-hint">No completed retrospectives in this date range.</div>}

            {availableInstances.length > 0 && (
              <>
                <div className="session-summary-selection-heading">
                  <div className="session-summary-selection-count">
                    {selectedIds.length} session{selectedIds.length === 1 ? "" : "s"} selected
                  </div>
                  <button
                    type="button"
                    className="action-button secondary compact"
                    onClick={() => setSelectedIds(
                      selectedIds.length === availableInstances.length
                        ? []
                        : availableInstances.map((item) => item.serviceInstanceId),
                    )}
                  >
                    {selectedIds.length === availableInstances.length ? "Clear all" : "Select all"}
                  </button>
                </div>

                <div className="session-summary-session-picker">
                  {availableInstances.map((instance) => {
                    const start = new Date(instance.startDateTime);
                    const end = new Date(instance.endDateTime);
                    const selected = selectedIds.includes(instance.serviceInstanceId);
                    return (
                      <button
                        key={instance.serviceInstanceId}
                        type="button"
                        className={`service-enrollment-item session-summary-session-option${selected ? " session-summary-session-option--selected" : ""}`}
                        onClick={() => toggle(instance.serviceInstanceId)}
                        aria-pressed={selected}
                      >
                        <div>
                          <div className="service-enrollment-name">{dateFormatter.format(start)}</div>
                          <div className="field-hint session-summary-session-time">
                            {timeFormatter.format(start)} - {timeFormatter.format(end)}
                          </div>
                        </div>
                        {selected && <EventAvailableIcon fontSize="small" />}
                      </button>
                    );
                  })}
                </div>

                <div className="session-summary-load-toolbar">
                  <div className="session-summary-load-guidance">
                    {selectedIds.length === 0
                      ? "Select one or more sessions, then create the summary."
                      : `${selectedIds.length} session${selectedIds.length === 1 ? "" : "s"} ready to summarise.`}
                  </div>
                  <div className="session-summary-load-actions">
                    <button type="button" className="action-button primary session-summary-create-button" disabled={selectedIds.length === 0} onClick={generateSummary}>
                      <AssessmentIcon fontSize="small" />
                      Create draft{selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}
                    </button>
                  </div>
                </div>
                <div className="session-summary-ai">
                  <AiTextImprovement
                    documentType="SessionSummary"
                    actions={[{ value: "Summarize", label: "Summarise selected sessions" }]}
                    fields={aiSourceFields}
                    allowFieldSelection={false}
                    allowFocus={false}
                    deferIndividualApply
                    guidance="Check the combined report against the selected session reviews before accepting it."
                    onApply={(values) => {
                      setSummary((current) => ({ ...current, ...values }));
                      setSummaryId(null);
                    }}
                  />
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="session-summary-loaded-toolbar">
              <button type="button" className="action-button secondary session-summary-change-button" onClick={changeSessions}>
                <ViewWeekIcon fontSize="small" />
                Change sessions
              </button>
              <div className="session-summary-loaded-actions">
                <button type="button" className="action-button primary session-summary-save-button" onClick={saveSummary} disabled={saving}>
                  <SaveOutlinedIcon fontSize="small" />
                  <span>{saving ? "Saving…" : summaryId ? "Update summary" : "Save summary"}</span>
                </button>
                <button type="button" className="action-button secondary session-summary-print-button" onClick={() => window.print()}>
                  <PrintIcon fontSize="small" />
                  <span>Print report</span>
                </button>
              </div>
            </div>

            <section className="card session-summary-editor">
              <div className="session-summary-editor-heading">
                <div>
                  <div className="card-title">Summary report</div>
                  <div className="field-hint">Review and edit the combined content before using it.</div>
                </div>
              </div>
              {fields.map(({ key, label }) => (
                <div className="form-group" key={key}>
                  <label className="form-label" htmlFor={`summary-${key}`}>{label}</label>
                  <textarea
                    id={`summary-${key}`}
                    className="form-input"
                    rows={4}
                    value={summary[key] ?? ""}
                    onChange={(event) => setSummary({ ...summary, [key]: event.target.value })}
                  />
                </div>
              ))}
            </section>
          </>
        )}
      </section>
    </div>
  );
}
